import { ActionIcon, Box, Button, Divider, Group, Loader, Popover, Space, Text, TextInput, ThemeIcon, Tooltip, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react';
import { RuleEditCard } from './Design';
import { RuleEditor } from './Model';
import { MouseEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import { useDiMemo } from '@root/Services/DI';
import { AnchorButton } from '@root/Design/Primitives';
import { EventEmitter, useEvent, useEventValue, useToggle } from '@root/Services/EventEmitter';
import { injectable } from 'tsyringe';
import { TagAutomationRule, TagRuleSyntax } from '@apis/TagManager/model';
import { makeAutoObservable } from 'mobx';
import {
    AwsOUAccountGroupRuleOptions,
    AwsOUAccountGroupRuleOptionsGroupingType,
    BaseAwsResource,
    BaseAzureResource,
    IQueryExpr,
} from '@apis/Resources/model';
import { postResourcesQuery } from '@apis/Resources';
import { queryBuilder, ValuesGroupOtherText } from '@root/Services/QueryExpr';
import styled from '@emotion/styled';
import { BaseResource } from '@root/Components/Resources/ResourcesGrid';
import { getAccountGetAccounts, getOrganizationGetOrganizations } from '@apis/Customers';
import { useCompany } from '@root/Components/Router/CompanyContent';
import { useFmtSvc } from '@root/Services/FormatService';
import { useAsync, useAsyncAbortable } from '@react-hookz/web';
import { FillerSwitch } from '@root/Design/Filler';
import { ResourceDetailsOpener } from '@root/Components/Resources/ResourceDetails';
import { Node } from '@root/Components/VirtualTree/Node';
import { VirtualTree, ITypedTreeConfig, ITreeItemState } from '@root/Components/VirtualTree';
import { Picker } from '@root/Components/Picker/Picker';
import { AnnotatedTagKey } from '@root/Components/Resources/Common/ResourceTags';
import { ChevronsDown, ChevronsUp, ChevronDown, ChevronRight, Eraser, LayoutSidebarRightExpand, Refresh, Tool } from 'tabler-icons-react';
import { EmptyDataText } from '@root/Components/Text/VisibleSpaces';
import { EllipsisTextEl } from '@root/Design/Text';
import { VirtualTreeModel } from '@root/Components/VirtualTree/VirtualTreeModel';

@injectable()
export class AwsOrgEntityEditor {
    public options: AwsOUAccountGroupRuleOptions = {};
    public syntax: TagRuleSyntax = {};
    public ruleEditor: RuleEditor = {} as RuleEditor;
    public filter: IQueryExpr = {};
    public rule?: TagAutomationRule = undefined;
    public constructor() {
        makeAutoObservable(this);
    }

    public init(ruleEditor: RuleEditor) {
        const rule = ruleEditor.rule!;
        rule.Parameters ??= {};
        rule.Parameters.Syntax ??= {};
        rule.Parameters.Syntax.ResourceGroupRuleOptions = {};
        rule.Parameters.Syntax.AwsOUAccountGroupRuleOptions ??= {};
        rule.Parameters.Syntax.AwsOUAccountGroupRuleOptions.SelectedTags ??= [];
        rule.Parameters.Syntax.AwsOUAccountGroupRuleOptions.SelectedAccounts ??= [];
        rule.Parameters.Syntax.AwsOUAccountGroupRuleOptions.SelectedOrganizations ??= [];
        rule.Parameters.Syntax.AwsOUAccountGroupRuleOptions.GroupingType ??= AwsOUAccountGroupRuleOptionsGroupingType.OrganizationalUnit;
        this.options = rule.Parameters.Syntax.AwsOUAccountGroupRuleOptions ??= {};
        this.syntax = rule.Parameters.Syntax ??= {};
        this.ruleEditor = ruleEditor;
        this.rule = rule;
        return this;
    }

    public getSelectedTags(id: string) {
        return this.options.SelectedTags?.find((t) => t.EntityId === id)?.SelectedTags ?? [];
    }

    public setSelectedTags = (id: string, selectedTagKeys: string[]) => {
        let selection = this.options.SelectedTags!.find((t) => t.EntityId === id);
        if (!selection) {
            this.options.SelectedTags!.push({ SelectedTags: [], EntityId: id });
            selection = this.options.SelectedTags![this.options.SelectedTags!.length - 1];
        }
        selection.SelectedTags = selectedTagKeys;
    };

    public clearAll = () => {
        this.options.SelectedTags = [];
    };

    public setFilter(entityIds: string[]) {
        if (entityIds.length !== 0) {
            var query = queryBuilder<BaseResource>()
                .where((b) =>
                    b.and(
                        b.model.ResourceType!.ne('Organization Account'),
                        b.model.ResourceType!.ne('Organization Root'),
                        b.model.ResourceType!.ne('Organization Unit'),
                        b.model.AccountName!.eq(entityIds)
                    )
                )
                .build();
            this.rule!.Parameters!.Filter! = query.Where!;
        }
    }

    public setApplyAllTags(applyAllTags: boolean) {
        this.options.ApplyAllTags = applyAllTags;
    }

    public setGroupType(groupType: AwsOUAccountGroupRuleOptionsGroupingType) {
        this.options.GroupingType = groupType;
    }

    public getErrors() {
        const result: string[] = [];
        return result;
    }
}

export const AwsOrgEntityGroupActionCard = observer(function AwsOrgEntityGroupActionCard({ ruleEditor }: { ruleEditor: RuleEditor }) {
    const awsOrgEntityEditor = useDiMemo(AwsOrgEntityEditor, [ruleEditor], (r) => r.init(ruleEditor));

    return (
        <>
            <RuleEditCard
                title="Select Tags to Copy"
                description="Select Account and Organizational Unit tags which should be copied to their resources"
            >
                <AwsOrgTagSelector editor={awsOrgEntityEditor} />
            </RuleEditCard>
        </>
    );
});

type AwsOrgItem = {
    id: string;
    externalId: string;
    name: string;
    type: 'Organizational Unit' | 'Account' | 'Organization Root';
    availableTagKeys: string[];
    selectedTagKeys: string[];
    selectionChanged: EventEmitter<void>;
    children: undefined | Array<AwsOrgItem>;
    searchText: string;
};
const AwsOrgTagSelector = observer(function AwsOrgTagSelector({ editor }: { editor: AwsOrgEntityEditor }) {
    const company = useCompany();
    const theme = useMantineTheme();

    const selectedResource = useMemo(() => new EventEmitter<BaseAwsResource | undefined>(undefined), []);
    const viewInvalidated = useMemo(() => EventEmitter.empty(), []);
    const [treeModel, setTreeModel] = useState<VirtualTreeModel<AwsOrgItem>>();
    const [orgItems, setOrgItems] = useState<AwsOrgItem[]>();

    const getOrgData = useCallback(async () => {
        const [accts, orgs] = await Promise.all([getAccountGetAccounts(), getOrganizationGetOrganizations({ companyId: company!.Id })]);
        return { accts, orgs };
    }, []);
    const [{ result: orgData }, { execute: getOrgs, abort: abortGetOrgs }] = useAsyncAbortable(getOrgData, null);
    type OrgData = typeof orgData;

    const getOrgTagData = useCallback(async () => {
        const result = await queryBuilder<{ Id: string; Name: string; 'Tags.Key': string; ResourceType: string }>()
            .where((b) => b.model.ResourceType!.eq(['Organizational Unit', 'Organization Root', 'Organization Account']))
            .select((b) => ({
                id: b.model.Id,
                name: b.model.Name,
                tagKeys: b.values(b.model['Tags.Key'], undefined, ValuesGroupOtherText),
                tagCt: b.count(),
            }))
            .execute(postResourcesQuery);

        const grouped = (result.Results ?? []).reduce((res, r) => {
            let group = res.get(r.id);
            if (!group) {
                res.set(r.id, (group = []));
            }
            group.push(r);
            return res;
        }, new Map<string, { id: string; name: string; tagKeys: string }[]>());

        return [...grouped.entries()].map(([k, v]) => ({
            id: k,
            name: v[0].name,
            tagKeys: v.map((r) => r.tagKeys).filter((r) => r !== ValuesGroupOtherText),
        }));
    }, []);
    const [{ status, result: orgTags }, { execute: getOrgTags, abort: abortGetOrgTags }] = useAsyncAbortable(getOrgTagData, null);
    type OrgTags = typeof orgTags;

    const createOrgItems = (orgData: OrgData, orgTags: OrgTags) => {
        let result: AwsOrgItem[] = [];
        if (orgData?.accts?.length && orgData.orgs?.length && orgTags?.length) {
            const parentLookup = new Map<string, string | number | null | undefined>();
            const orgExtIdLookup = new Map<number, string>();
            for (const org of orgData.orgs) {
                parentLookup.set(org.AwsOrganizationalUnitId ?? '', org.ParentId);
                orgExtIdLookup.set(org.Id ?? 0, org.AwsOrganizationalUnitId ?? '');
            }
            for (const acct of orgData.accts) {
                parentLookup.set(acct.AwsAccountId ?? '', acct.OrganizationUnitId);
            }
            const itemLookup = new Map<string, AwsOrgItem>();
            const childLookup = new Map<string | number, AwsOrgItem[]>();

            const sorted = orgTags.sort((a, b) => a.name.localeCompare(b.name));

            for (const { id, name, tagKeys } of sorted) {
                const [, , externalId] = id.split('.');
                let parentId = parentLookup.get(externalId) ?? null;
                parentId = typeof parentId !== 'number' ? parentId : orgExtIdLookup.get(parentId) ?? parentId;
                const selectionChanged = EventEmitter.empty();
                const type = externalId.match(/^\d{12}$/) ? 'Account' : externalId.startsWith('r-') ? 'Organization Root' : 'Organizational Unit';

                const item: AwsOrgItem = {
                    id,
                    externalId,
                    name,
                    type,
                    availableTagKeys: tagKeys,
                    get selectedTagKeys() {
                        return editor.getSelectedTags(id);
                    },
                    get children() {
                        return childLookup.get(externalId) ?? undefined;
                    },
                    selectionChanged,
                    searchText: [name.toLowerCase(), externalId.toLowerCase(), ...tagKeys.map((k) => k.toLowerCase())].join(' '),
                };

                if (parentId) {
                    let children = childLookup.get(parentId);
                    if (!children) {
                        childLookup.set(parentId, (children = []));
                    }
                    children.push(item);
                } else {
                    result.push(item);
                }

                itemLookup.set(id, item);
            }
        }
        return result;
    };

    useEffect(() => {
        getOrgs();
        getOrgTags();
        return () => {
            abortGetOrgs();
            abortGetOrgTags();
        };
    }, []);

    useEffect(() => {
        setOrgItems(createOrgItems(orgData!, orgTags!));
    }, [orgData, orgTags]);

    const config = useMemo(() => {
        const result: ITypedTreeConfig<AwsOrgItem> = {
            itemHeight: 50,
            canExpand: (item) => item.children != null,
            childAccessor: (item) => item.children,
            onModelLoaded: setTreeModel,
            renderNode: (item, state) => (
                <AwsOrgItemRow item={item} state={state} onShowDetail={selectedResource.emit} onSelectionChange={editor.setSelectedTags} />
            ),
        };
        return result;
    }, []);

    const anyExpanded = treeModel?.items.some((o) => treeModel.isExpanded(o.item));
    const handleToggleAll = useCallback(() => {
        if (treeModel) {
            if (anyExpanded) {
                treeModel.collapseAll();
            } else {
                treeModel.expandAll();
            }
            viewInvalidated.emit();
        }
    }, [treeModel, anyExpanded]);
    const handleClearAll = useCallback(() => {
        editor.clearAll();
        viewInvalidated.emit();
    }, [editor]);
    const handleFilterChange = useCallback(
        (filter: string) => {
            const lcFilters = filter
                .toLowerCase()
                .split(' ')
                .filter((f) => f);
            const filterHandler = !lcFilters.length ? undefined : (item: AwsOrgItem) => lcFilters.some((f) => item.searchText.includes(f));
            treeModel?.setFilter(filterHandler);
        },
        [treeModel]
    );

    useEffect(() => {
        if (treeModel && orgItems?.length) {
            treeModel.expandAll();
        }
        viewInvalidated.emit();
    }, [orgItems, treeModel]);

    useEvent(viewInvalidated);

    return (
        <Box sx={{ borderRadius: theme.radius.md, border: `1px solid ${theme.colors.gray[3]}` }}>
            <Group py="xs" px="xs" position="apart" spacing="xs">
                <Tooltip label={anyExpanded ? 'Collapse all' : 'Expand all'}>
                    <ActionIcon color="primary" disabled={!treeModel} onClick={handleToggleAll}>
                        {anyExpanded ? <ChevronsUp size={18} /> : <ChevronsDown size={18} />}
                    </ActionIcon>
                </Tooltip>
                <TreeFilter onChange={handleFilterChange} />
                <Space sx={{ flex: 1 }} />
                <Tooltip label="Clear all selected tags">
                    <ActionIcon color="primary" onClick={handleClearAll}>
                        <Eraser size={18} />
                    </ActionIcon>
                </Tooltip>
                <Tooltip label="Refresh">
                    <ActionIcon color="primary" onClick={getOrgTags}>
                        <Refresh size={18} />
                    </ActionIcon>
                </Tooltip>
            </Group>
            <Divider />
            <Box sx={{ height: 500 }}>
                <FillerSwitch loading={status === 'loading'} noData={!orgItems?.length}>
                    {() => <VirtualTree data={orgItems!} config={config} />}
                </FillerSwitch>
                <ResourceDetails selectedResource={selectedResource} />
            </Box>
        </Box>
    );
});

function TreeFilter({ onChange }: { onChange: (filter: string) => void }) {
    const [value, setValue] = useState('');
    useEffect(() => onChange(value), [value, onChange]);
    return (
        <TextInput
            sx={{ input: { height: 28, minHeight: 28 } }}
            placeholder="Filter..."
            value={value}
            onChange={(evt) => setValue(evt.currentTarget.value)}
        />
    );
}

function ResourceDetails({ selectedResource }: { selectedResource: EventEmitter<BaseAwsResource | undefined> }) {
    const resource = useEventValue(selectedResource);
    const clear = useCallback(() => selectedResource.emit(undefined), [selectedResource]);
    return (
        <ResourceDetailsOpener
            onClose={clear}
            platform={resource?.CloudPlatform ?? 'Aws'}
            resourceId={resource?.Id}
            resourceType={resource?.ResourceType ?? ''}
            resource={resource}
        />
    );
}

function AwsOrgItemRow({
    onShowDetail,
    onSelectionChange,
    item,
    state,
}: {
    onShowDetail: (item: BaseAwsResource) => void;
    onSelectionChange: (id: string, keys: string[]) => void;
    item: Node<AwsOrgItem>;
    state: ITreeItemState<AwsOrgItem>;
}) {
    const fmtSvc = useFmtSvc();
    const { id, externalId, name, type, availableTagKeys, selectedTagKeys, selectionChanged } = item.item;
    const [{ status }, { execute: loadResource }] = useAsync(async (id: string) =>
        queryBuilder<BaseAzureResource>()
            .where((b) => b.model.Id!.eq(id))
            .execute(postResourcesQuery)
            .then((r) => r.Results?.[0] as BaseAwsResource | undefined)
    );
    useEvent(selectionChanged);

    const extIdName = type === 'Account' ? fmtSvc.awsAccountId(externalId) : externalId;
    const handleShowDetail: MouseEventHandler<HTMLAnchorElement> = useCallback(
        (evt) => {
            loadResource(item.item.id).then((resource) => {
                if (resource) {
                    onShowDetail(resource);
                }
            });
            evt.stopPropagation();
        },
        [id, onShowDetail]
    );
    const handleToggle = useCallback(() => {
        state.model.toggle(item.item);
    }, [item, state]);
    const handleSelectionChange = useCallback(
        (keys: { key: string }[]) => {
            onSelectionChange(
                id,
                keys.map((k) => k.key)
            );
            selectionChanged.emit();
        },
        [id, selectionChanged]
    );
    const availableKeys = useMemo(() => availableTagKeys.map((key) => ({ key })), [availableTagKeys]);
    const selectedKeys = useMemo(() => availableKeys.filter((key) => selectedTagKeys.includes(key.key)), [selectedTagKeys, availableKeys]);

    return (
        <AwsOrgItemRowEl depth={item.depth} hasChildren={item.hasChildren} onClick={handleToggle}>
            {!item.hasChildren ? <Box></Box> : <ActionIcon>{state.isExpanded(item.item) ? <ChevronDown /> : <ChevronRight />}</ActionIcon>}
            <Box>
                <Group noWrap sx={{ height: 20 }} spacing={4}>
                    <Text color="dimmed" size="xs" span>
                        {type}
                    </Text>
                    <AnchorButton
                        size="xs"
                        text={extIdName}
                        onClick={handleShowDetail}
                        iconPosition="right"
                        iconOnHover={status !== 'loading'}
                        icon={status !== 'loading' ? <LayoutSidebarRightExpand size={20} /> : <Loader size="xs" />}
                    />
                </Group>
                <Text size="sm">{name || 'Unnamed'}</Text>
            </Box>
            <Group noWrap>
                <TagPicker availableKeys={availableKeys} selectedKeys={selectedKeys} onChange={handleSelectionChange} />
            </Group>
        </AwsOrgItemRowEl>
    );
}

const AwsOrgItemRowEl = styled.div<{ depth: number; hasChildren?: boolean }>`
    display: grid;
    grid-template-columns: 20px 1fr 400px;
    padding: 0 6px;
    align-items: center;
    gap: ${(p) => p.theme.spacing.sm}px;
    padding-left: ${(p) => p.depth * 20 + 6}px;
    height: 50px;
    cursor: ${(p) => (p.hasChildren ? 'pointer' : 'default')};
    border-bottom: solid 1px ${(p) => p.theme.colors.gray[2]};
    &:hover {
        background-color: ${(p) => (!p.hasChildren ? p.theme.colors.gray[1] : p.theme.colors.primary[1])};
    }
`;

function TagPicker({
    availableKeys,
    selectedKeys,
    onChange,
}: {
    availableKeys: { key: string }[];
    selectedKeys: { key: string }[];
    onChange: (keys: { key: string }[]) => void;
}) {
    const [visible, { close, toggle }] = useToggle(false);
    const handleOpenerClick: MouseEventHandler<HTMLElement> = useCallback(
        (evt) => {
            toggle();
            evt.stopPropagation();
        },
        [toggle]
    );
    const handleClick: MouseEventHandler<HTMLElement> = useCallback((evt) => evt.stopPropagation(), []);

    return (
        <Popover withinPortal withArrow shadow="lg" opened={visible} onClose={close} offset={0}>
            <Popover.Target>
                <PickerSelections onClick={handleOpenerClick}>
                    <Text size="xs" color="dimmed">
                        Selected Tags ({selectedKeys.length} / {availableKeys.length})
                    </Text>
                    <Text component={EllipsisTextEl} size="sm">
                        {!availableKeys.length ? (
                            <EmptyDataText text="None Available" />
                        ) : !selectedKeys.length ? (
                            <EmptyDataText text="None Selected" />
                        ) : (
                            selectedKeys?.map((k) => k.key).join(', ') ?? ''
                        )}
                    </Text>
                </PickerSelections>
            </Popover.Target>
            <Popover.Dropdown p={0} onClick={handleClick}>
                <Picker
                    height={300}
                    mode="multiple"
                    renderItem={(item) => <AnnotatedTagKey tagKey={item.key} />}
                    nameAccessor={(v) => v.key}
                    items={availableKeys}
                    selections={selectedKeys}
                    onChange={onChange}
                />
            </Popover.Dropdown>
        </Popover>
    );
}

const PickerSelections = styled.div`
    cursor: pointer;
    border: solid 1px transparent;
    border-radius: ${(p) => p.theme.radius.sm}px;
    padding: 2px ${(p) => p.theme.spacing.sm}px;
    flex: 1;
    overflow: hidden;

    &:hover {
        border-color: ${(p) => p.theme.colors.gray[4]};
        background: ${(p) => p.theme.colors.gray[0]};
    }
`;
